/**
 * Shared time helpers for employee time-entry surfaces (Weekly grid + Calendar).
 *
 * The canonical duration model is whole minutes (duration_minutes on the API).
 * These helpers convert between what a user types and that minute count.
 */

/**
 * Parse a user-entered day total into whole minutes.
 *
 * Accepted formats:
 *   - Hours-based (a separator is present):
 *       "7:30" -> 450, "7.5" -> 450, "7,5" -> 450
 *   - Empty string -> 0
 *   - Colon-free "hhmm" (pure digits, 1-4 chars):
 *       length <= 2  -> whole hours   ("8" -> 480, "12" -> 720)
 *       length === 3 -> H + MM        ("730" -> 450, "45" is len 2 so 0h45m? no)
 *       length === 4 -> HH + MM       ("0730" -> 450, "1230" -> 750)
 *
 * Note on the 2-digit rule: per spec a 1-2 digit pure-number is WHOLE HOURS,
 * so "45" -> 45 hours which exceeds the day cap and is rejected. To enter
 * 45 minutes the user types "045" or "0045" (or "0.75" / "0:45").
 *
 * Rejects (returns { error: true }) when minutes >= 60 or the total > 1440
 * (24h), or the input is otherwise unparseable.
 *
 * @param {string} raw
 * @returns {{minutes: number} | {error: true}}
 */
export const parseTimeToMinutes = (raw) => {
  const value = (raw || '').trim();
  if (value === '') return { minutes: 0 };

  // Hours-based parse when an explicit H:MM separator is present.
  if (value.includes(':')) {
    const [h, m] = value.split(':');
    const hours = parseInt(h, 10);
    const mins = parseInt(m, 10);
    if (Number.isNaN(hours) || Number.isNaN(mins) || mins < 0 || mins > 59 || hours < 0) {
      return { error: true };
    }
    const total = hours * 60 + mins;
    if (total > 1440) return { error: true };
    return { minutes: total };
  }

  // Decimal hours when a '.'/',' separator is present.
  if (value.includes('.') || value.includes(',')) {
    const decimal = parseFloat(value.replace(',', '.'));
    if (Number.isNaN(decimal) || decimal < 0 || decimal > 24) return { error: true };
    return { minutes: Math.round(decimal * 60) };
  }

  // Colon-free "hhmm": require a pure 1-4 digit string. Rejecting anything with
  // a stray sign/letter (e.g. "-5", "1e3") instead of silently stripping it
  // avoids coercing garbage into a positive value.
  if (!/^\d{1,4}$/.test(value)) return { error: true };
  const digits = value;

  let hours;
  let mins;
  if (digits.length <= 2) {
    hours = parseInt(digits, 10);
    mins = 0;
  } else if (digits.length === 3) {
    hours = parseInt(digits.slice(0, 1), 10);
    mins = parseInt(digits.slice(1), 10);
  } else {
    hours = parseInt(digits.slice(0, 2), 10);
    mins = parseInt(digits.slice(2), 10);
  }

  if (Number.isNaN(hours) || Number.isNaN(mins) || mins >= 60) return { error: true };
  const total = hours * 60 + mins;
  if (total > 1440) return { error: true };
  return { minutes: total };
};

/**
 * Format whole minutes as "H:MM" (e.g. 450 -> "7:30").
 * @param {number} minutes
 * @returns {string}
 */
export const formatMinutes = (minutes) => {
  const total = Math.max(0, Math.round(Number(minutes) || 0));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};
